import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FormGroup } from 'react-bootstrap';
import { FormItem } from './';
import { filter, flatMap, groupBy, map, sortBy, uniqBy } from 'lodash';
/* ==========================================================================
 * PURE HELPER FUNCTIONS
 * ========================================================================== */
export const groupUsersByTeam = (teamMembers) => {
    // 1. Isolate ungrouped members
    const ungroupedMembers = filter(teamMembers, (member) => !member.teamIDs || member.teamIDs.length === 0);
    // 2. Unroll the members that DO have teams into single-level tuples
    const teamMemberPairs = flatMap(filter(teamMembers, (member) => member.teamIDs && member.teamIDs.length > 0), (member) => {
        const teamIds = member.teamIDs || [];
        const teamNames = member.teamNames || [];
        return teamIds.map((teamId, index) => ({
            teamId,
            teamName: teamNames[index] || `Team ${teamId}`,
            member
        }));
    });
    // 3. Group the tuples into a dictionary keyed by teamId
    const groupedMap = groupBy(teamMemberPairs, 'teamId');
    // 4. Shape the dictionary back into GroupedTeam[] and sort alphabetically
    const namedTeams = sortBy(map(groupedMap, (pairs, teamId) => ({
        id: teamId,
        name: pairs[0].teamName,
        members: map(pairs, 'member')
    })), (team) => team.name.toLowerCase());
    // 5. Append the "Ungrouped" team to the end if necessary
    return ungroupedMembers.length > 0
        ? namedTeams.concat([
            { id: 'ungrouped', name: 'Ungrouped', members: ungroupedMembers }
        ])
        : namedTeams;
};
const MemberLabel = ({ user }) => (React.createElement("div", { className: 'member-text-container' },
    user.firstName,
    " ",
    user.lastName,
    ' ',
    React.createElement("small", { className: 'member-email-text' },
        "(",
        user.email,
        ")")));
/* ==========================================================================
 * CUSTOM HOOKS
 * ========================================================================== */
const useOnClickOutside = (ref, handler) => {
    const handlerRef = useRef(handler);
    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);
    useEffect(() => {
        const listener = (event) => {
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handlerRef.current(event);
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref]);
};
/* ==========================================================================
 * PRESENTATIONAL COMPONENTS
 * ========================================================================== */
const CustomCheckbox = ({ checked, indeterminate }) => {
    const isHighlighted = checked || indeterminate;
    const classes = [
        'custom-checkbox',
        isHighlighted ? 'highlighted' : '',
        checked ? 'checked' : ''
    ]
        .filter(Boolean)
        .join(' ');
    return (React.createElement("div", { className: classes },
        checked && React.createElement("div", { className: 'checkmark' }, "\u2713"),
        indeterminate && !checked && React.createElement("div", { className: 'indeterminate-line' })));
};
/* ==========================================================================
 * MAIN COMPONENT: TeamUserSelector
 * ========================================================================== */
export const TeamUserSelector = ({ teamMembers = [], selected = [], onChange, onSearch, searchText = '', label = 'Who is this for?', validation, currentUser }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    useOnClickOutside(containerRef, () => setIsOpen(false));
    // --- 1. MEMOIZED DERIVATIONS ---
    const selectedIds = useMemo(() => {
        return selected.map((selectedMember) => selectedMember.user.id);
    }, [selected]);
    const groupedTeams = useMemo(() => {
        return groupUsersByTeam(teamMembers);
    }, [teamMembers]);
    const currentUserMember = useMemo(() => {
        if (!currentUser)
            return null;
        return teamMembers.find((member) => member.user.id === currentUser.id);
    }, [teamMembers, currentUser]);
    const visibleOptions = useMemo(() => {
        const allGroupedMembers = flatMap(groupedTeams, (team) => team.members);
        const combinedMembers = currentUserMember
            ? [currentUserMember].concat(allGroupedMembers)
            : allGroupedMembers;
        return uniqBy(combinedMembers, (member) => member.user.id);
    }, [currentUserMember, groupedTeams]);
    const visibleOptionIds = useMemo(() => {
        return visibleOptions.map((visibleMember) => visibleMember.user.id);
    }, [visibleOptions]);
    const isAllSelected = visibleOptionIds.length > 0 &&
        visibleOptionIds.every((optionId) => selectedIds.includes(optionId));
    const isSomeSelected = !isAllSelected &&
        visibleOptionIds.some((optionId) => selectedIds.includes(optionId));
    // --- 2. ORCHESTRATORS (Event Handlers) ---
    const handleSelectAllToggle = useCallback(() => {
        if (isAllSelected) {
            // Deselect all currently visible options
            onChange(selected.filter((selectedMember) => !visibleOptionIds.includes(selectedMember.user.id)));
        }
        else {
            // Add all visible options that aren't already selected
            const membersToAdd = visibleOptions.filter((visibleMember) => !selectedIds.includes(visibleMember.user.id));
            onChange(selected.concat(membersToAdd));
        }
    }, [
        isAllSelected,
        selected,
        selectedIds,
        visibleOptions,
        visibleOptionIds,
        onChange
    ]);
    const handleUserToggle = useCallback((member) => {
        const userId = member.user.id;
        if (selectedIds.includes(userId)) {
            onChange(selected.filter((selectedMember) => selectedMember.user.id !== userId));
        }
        else {
            onChange(selected.concat([member]));
        }
    }, [selected, selectedIds, onChange]);
    const handleTeamToggle = useCallback((team) => {
        const teamMemberIds = team.members.map((teamMember) => teamMember.user.id);
        const isTeamAllSelected = teamMemberIds.every((memberId) => selectedIds.includes(memberId));
        if (isTeamAllSelected) {
            onChange(selected.filter((selectedMember) => !teamMemberIds.includes(selectedMember.user.id)));
        }
        else {
            const membersToAdd = team.members.filter((teamMember) => !selectedIds.includes(teamMember.user.id));
            onChange(selected.concat(membersToAdd));
        }
    }, [selected, selectedIds, onChange]);
    // --- 3. RENDER HELPERS ---
    const isFilled = searchText.trim().length > 0 || selected.length > 0;
    const inputClasses = `form-control ${isOpen ? 'focus' : ''} ${isFilled ? 'filled' : ''}`;
    return (React.createElement("div", { ref: containerRef },
        React.createElement(FormGroup, { validationState: validation ? 'error' : null, className: 'tags-input' },
            React.createElement("div", { className: 'form-item text-item search-item autocomplete no-icon no-margin' },
                React.createElement("input", { type: 'text', className: inputClasses, value: searchText, onFocus: () => setIsOpen(true), onChange: (event) => {
                        onSearch(event.target.value);
                        setIsOpen(true);
                    } }),
                React.createElement("label", null, label),
                isOpen && (groupedTeams.length > 0 || currentUserMember) && (React.createElement("div", { className: 'team-selector-dropdown' },
                    teamMembers.length > 0 && (React.createElement("div", { className: 'team-header-row green-row', onClick: handleSelectAllToggle },
                        React.createElement(CustomCheckbox, { checked: isAllSelected, indeterminate: isSomeSelected }),
                        React.createElement("strong", null, "Select All"))),
                    currentUserMember && (React.createElement("div", { className: 'team-group-wrapper team-header-row', onClick: () => handleUserToggle(currentUserMember) },
                        React.createElement(CustomCheckbox, { checked: selectedIds.includes(currentUser.id) }),
                        React.createElement(MemberLabel, { user: currentUserMember.user }))),
                    groupedTeams.map((team) => {
                        const memberIds = team.members.map((teamMember) => teamMember.user.id);
                        const isTeamAllChecked = memberIds.every((memberId) => selectedIds.includes(memberId));
                        const isTeamSomeChecked = !isTeamAllChecked &&
                            memberIds.some((memberId) => selectedIds.includes(memberId));
                        return (React.createElement("div", { key: team.id, className: 'team-group-wrapper' },
                            React.createElement("div", { className: 'team-header-row', onClick: () => handleTeamToggle(team) },
                                React.createElement(CustomCheckbox, { checked: isTeamAllChecked, indeterminate: isTeamSomeChecked }),
                                React.createElement("strong", null, team.name)),
                            React.createElement("div", { className: 'team-members-wrapper' }, team.members.map((member) => {
                                const user = member.user;
                                const isUserSelected = selectedIds.includes(user.id);
                                return (React.createElement("div", { key: user.id, className: 'member-row', onClick: () => handleUserToggle(member) },
                                    React.createElement(CustomCheckbox, { checked: isUserSelected }),
                                    React.createElement(MemberLabel, { user: user })));
                            }))));
                    })))),
            selected.length > 0 && (React.createElement(FormItem, null,
                React.createElement("div", { className: 'bootstrap-tagsinput' }, selected.map((selectedMember) => {
                    const user = selectedMember.user;
                    return (React.createElement("span", { key: `tag-${user.id}`, className: 'tag label label-info' },
                        user.firstName,
                        " ",
                        user.lastName,
                        React.createElement("span", { "data-role": 'remove', onClick: (event) => {
                                event.stopPropagation();
                                handleUserToggle(selectedMember);
                            } })));
                })))))));
};
export default TeamUserSelector;



//////////////////
// WEBPACK FOOTER
// ./src/containers/components/teamUserSelector.tsx
// module id = 1817
// module chunks = 0